import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { JobOffersSearch } from "@/components/jobs/JobOffersSearch";
import { JobOffersList } from "@/components/jobs/JobOffersList";
import { useNavigate } from "react-router-dom";
import { SortField, SortOrder, ViewMode, CandidatesViewOptions } from "@/components/candidates/CandidatesViewOptions";
import { useState, useEffect } from "react";
import { JobData, useJobsData } from "@/hooks/jobs/useJobsData";
import { useTranslation } from "react-i18next";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Job {
  id: string;
  title: string;
  company_id: string;
  company_name?: string;
  status?: string;
  created_at: string;
  skills?: string[];
  description?: string;
  [key: string]: any;
}

interface CompanyOffersTabProps {
  jobs: Job[];
  loading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  companyId: string;
}

export const CompanyOffersTab = ({
  jobs,
  loading,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
  companyId
}: CompanyOffersTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>("dateCreated");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const { addJob, fetchJobs } = useJobsData();
  const [companyName, setCompanyName] = useState<string>("");

  // Fetch company name when component mounts
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (companyId) {
        try {
          const { data, error } = await supabase
            .from('companies')
            .select('name')
            .eq('id', companyId)
            .single();
          
          if (error) {
            console.error("Error fetching company name:", error);
            return;
          }
          
          if (data) {
            setCompanyName(data.name);
          }
        } catch (error) {
          console.error("Failed to fetch company name:", error);
        }
      }
    };

    fetchCompanyName();
  }, [companyId]);

  const handleCreateJobOffer = () => {
    // Open the modal instead of navigating
    setIsAddJobModalOpen(true);
  };

  const handleAddJob = async (jobData: any) => {
    try {
      // Add company ID to job data if not already present
      const jobWithCompany = {
        ...jobData,
        companyId: companyId
      };
      
      // Use the addJob function from useJobsData
      const jobId = await addJob(jobWithCompany);
      
      if (jobId) {
        toast({
          title: t("common.success", "Success"),
          description: t("jobs.formWizard.createSuccess", "Job offer created successfully"),
        });
        
        // Close modal on success
        setIsAddJobModalOpen(false);
        
        // Return the job ID
        return jobId;
      } else {
        toast({
          title: t("common.error", "Error"),
          description: t("jobs.formWizard.createError", "Failed to create job offer"),
          variant: "destructive",
        });
        return undefined;
      }
    } catch (error) {
      console.error("Error adding job:", error);
      toast({
        title: t("common.error", "Error"),
        description: t("jobs.formWizard.createError", "Failed to create job offer"),
        variant: "destructive",
      });
      return undefined;
    }
  };

  // Transform jobs to JobData format for JobOffersList
  const transformedJobs: JobData[] = jobs.map(job => ({
    id: job.id,
    title: job.title,
    company: {
      id: job.company_id,
      name: job.company_name || t("companies.offers.unknownCompany")
    },
    skills: job.skills || [],
    createdAt: job.created_at,
    description: job.description
  }));

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">{t("companies.detail.tabs.offers")}</h3>
          <Button 
            onClick={handleCreateJobOffer}
            className="bg-digger-blue hover:bg-digger-blue-light"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("companies.offers.addJobOffer")}
          </Button>
        </div>
        
        <JobOffersSearch 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
        />
        
        <CandidatesViewOptions
          sortField={sortField}
          setSortField={setSortField}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />
        
        <JobOffersList 
          jobs={transformedJobs}
          isLoading={loading}
          viewMode={viewMode}
          refreshInterval={0} // No auto-refresh on the company job listings
          enableAutoRefresh={false}
        />

        {/* Add Job Modal */}
        <JobOffersAddModal
          isOpen={isAddJobModalOpen}
          onClose={() => setIsAddJobModalOpen(false)}
          onAddJob={handleAddJob}
          initialCompany={{
            id: companyId,
            name: companyName
          }}
        />
      </div>
    </div>
  );
};
